import { useEffect } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, GearSix } from '@phosphor-icons/react'
import { orgApi } from '@/api/organizations'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent } from '@/components/ui/card'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { OrgOverviewTab } from '@/components/organizations/OrgOverviewTab'
import { OrgSettingsTab } from '@/components/organizations/OrgSettingsTab'
import { OrgDangerZone } from '@/components/organizations/OrgDangerZone'
import { OrgUsersTab } from '@/components/organizations/OrgUsersTab'

export function OrganizationDetail() {
  const { orgId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const activeTab = searchParams.get('tab') || 'overview'

  const {
    data: org,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['organizations', 'detail', orgId],
    queryFn: () => orgApi.detail(orgId!),
    enabled: !!orgId,
  })

  // Set document title
  useEffect(() => {
    if (org) {
      document.title = `${org.name} - Copilot Core Admin`
    }
    return () => {
      document.title = 'Copilot Core Admin'
    }
  }, [org])

  function handleTabChange(value: string) {
    setSearchParams(
      value === 'overview' ? {} : { tab: value },
      { replace: true }
    )
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
        </div>
        <Skeleton className="h-10 w-64" />
        <div className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link
            to="/organizations"
            className="flex items-center gap-1 hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Organizations
          </Link>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-lg font-medium text-destructive mb-2">
              Failed to load organization
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {error.message}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Not found
  if (!org) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link
            to="/organizations"
            className="flex items-center gap-1 hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Organizations
          </Link>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-lg font-medium mb-2">Organization not found</p>
            <p className="text-sm text-muted-foreground mb-4">
              The organization you are looking for does not exist.
            </p>
            <Link to="/organizations">
              <Button variant="outline">Back to Organizations</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link
          to="/organizations"
          className="flex items-center gap-1 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Organizations
        </Link>
        <span>/</span>
        <span className="text-foreground">{org.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">{org.name}</h1>
          <Badge variant={org.is_active ? 'default' : 'destructive'}>
            {org.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleTabChange('settings')}
          className="text-muted-foreground"
        >
          <GearSix className="h-4 w-4 mr-1" />
          Edit Settings
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OrgOverviewTab
            org={org}
            onNavigateToUsers={() => handleTabChange('users')}
          />
        </TabsContent>

        <TabsContent value="users">
          <OrgUsersTab orgId={orgId!} org={org} />
        </TabsContent>

        <TabsContent value="settings">
          <div className="space-y-6">
            <OrgSettingsTab org={org} />
            <OrgDangerZone org={org} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
